// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Rewrite Optix Batch Dialog for I-9 documents
 *
 * Re-pushes the promoted run's metadata to Optix for an explicit, pasted list of object
 * ids — no re-OCR, no re-match. Unlike Re-run all Optix failures (which enumerates the
 * failed-write set server-side), this targets exactly the ids the admin pastes in, e.g.
 * from a report. Ids are split on any whitespace, comma, or newline and de-duplicated.
 * Each document is rewritten in its own processor run; missing or in-flight ids are
 * skipped by the server.
 */
import { useEffect, useMemo, useState } from 'react'
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Textarea,
} from '@/components/ui'
import { Upload } from 'lucide-react'

interface RewriteOptixBatchDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isRewriting: boolean
  onConfirm: (objectIds: string[]) => void
}

// Split pasted text on any whitespace, comma, or newline; drop blanks and duplicates
// while preserving first-seen order.
function parseObjectIds(text: string): string[] {
  const seen = new Set<string>()
  for (const token of text.split(/[\s,]+/)) {
    const id = token.trim()
    if (id) seen.add(id)
  }
  return Array.from(seen)
}

export const RewriteOptixBatchDialog: FC<RewriteOptixBatchDialogProps> = ({
  open,
  onOpenChange,
  isRewriting,
  onConfirm,
}) => {
  const [text, setText] = useState('')

  // Clear the field whenever the dialog reopens.
  useEffect(() => {
    if (open) setText('')
  }, [open])

  const objectIds = useMemo(() => parseObjectIds(text), [text])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Retry Optix for specific IDs
          </DialogTitle>
          <DialogDescription>
            Re-push the stored result to Optix for each pasted object id — no re-OCR or
            re-match. Each document is rewritten in its own run; documents that don&apos;t
            exist or are already being processed are skipped automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-4">
          <Label htmlFor="rewriteOptixObjectIds">Object IDs</Label>
          <Textarea
            id="rewriteOptixObjectIds"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste object ids separated by spaces, commas, or new lines"
            rows={8}
            className="font-mono"
          />
          <p className="text-xs text-muted-foreground">
            {objectIds.length} {objectIds.length === 1 ? 'id' : 'ids'} detected
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(objectIds)} disabled={isRewriting || objectIds.length === 0}>
            <Upload className="h-4 w-4 mr-1" />
            Retry Optix ({objectIds.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
